import { composeSourceTransform as composeSourceAffine, scaleRectToFrame } from '@lite-workspace/photobook-model';

export interface TrimRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function clampWindowToContent(window: TrimRect, content: TrimRect, minSize: number): TrimRect {
  const cRight = content.x + content.width;
  const cBottom = content.y + content.height;
  // Staying inside the content outranks the floor: on a decor thinner than minSize the raw floor would push
  // the window past the content edge and open the transparent margin trim promises never to create.
  const minW = Math.min(minSize, content.width);
  const minH = Math.min(minSize, content.height);
  const x = Math.min(Math.max(window.x, content.x), cRight - minW);
  const right = Math.min(Math.max(window.x + window.width, x + minW), cRight);
  const y = Math.min(Math.max(window.y, content.y), cBottom - minH);
  const bottom = Math.min(Math.max(window.y + window.height, y + minH), cBottom);
  return { x, y, width: right - x, height: bottom - y };
}

export function isUntrimmed(window: TrimRect, content: TrimRect, eps = 0.5): boolean {
  return (
    Math.abs(window.x - content.x) <= eps &&
    Math.abs(window.y - content.y) <= eps &&
    Math.abs(window.width - content.width) <= eps &&
    Math.abs(window.height - content.height) <= eps
  );
}

export function beginTrim(box: { rect: TrimRect; contentRect?: TrimRect }): { window: TrimRect; content: TrimRect } {
  const content = box.contentRect ?? box.rect;
  return { window: { ...box.rect }, content: { ...content } };
}

export type SourceAffine = readonly [number, number, number, number, number, number];

export function composeSourceTransform(transform: SourceAffine, oldFrame: TrimRect, newFrame: TrimRect): SourceAffine {
  return composeSourceAffine(transform, oldFrame, newFrame);
}

export function scaleContentToFrame(oldFrame: TrimRect, newFrame: TrimRect, content: TrimRect): TrimRect {
  return scaleRectToFrame(oldFrame, newFrame, content);
}
